import { useCallback, useEffect, useRef } from "react";

import type { ChatStory } from "../types";
import type { GroupedLogs } from "../utils/message-parsers";

interface UseScrollManagerProps {
  containerRef: React.RefObject<HTMLElement>;
  messages: (ChatStory | GroupedLogs)[];
  shouldAutoScroll?: boolean;
}

export function useScrollManager({
  containerRef,
  messages,
  shouldAutoScroll = true,
}: UseScrollManagerProps): {
  scrollToBottom: () => void;
} {
  const previousMessageCount = useRef(messages.length);

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [containerRef]);

  useEffect(() => {
    const hasNewMessages = messages.length > previousMessageCount.current;

    if (hasNewMessages && shouldAutoScroll) {
      scrollToBottom();
    }

    previousMessageCount.current = messages.length;
  }, [messages.length, shouldAutoScroll, scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
    const raf = requestAnimationFrame(scrollToBottom);

    return () => cancelAnimationFrame(raf);
  }, [scrollToBottom]);

  return {
    scrollToBottom,
  };
}
